import { useScenarioValidator } from "@/composables/validation/scenarioValidator";
import { computed } from "vue";
import type {
  HypotheticalFrequency,
  PredictiveCalcs,
  PumpTornadoChartContext,
  StartupBhpReportCalcRow
} from "@/libs/charting";
import type { DestructuredScenario } from "@/composables/useDestructureScenario";

const getPredictedRows = (
  destructuredScenario: DestructuredScenario,
  predictedCalcs: PredictiveCalcs,
  hypotheticalPumpFrequenciesHz: HypotheticalFrequency[]
): StartupBhpReportCalcRow[] => {
  const frequenciesToKeep = hypotheticalPumpFrequenciesHz.map(
    hf => hf.frequency
  );
  return Object.values(predictedCalcs)
    .filter(
      // remove the frequency at operating point
      c => frequenciesToKeep.indexOf(c.hypotheticalCalcs.pumpHz.value) > -1
    )
    .sort(
      (a, z) =>
        z.hypotheticalCalcs.pumpHz.value - a.hypotheticalCalcs.pumpHz.value
    )
    .map(c => {
      const { errors } = useScenarioValidator(
        destructuredScenario,
        c.hypotheticalCalcs
      );

      // Errors will cause the report to display N/A values.
      // This should match the errors that disable the Calc Flow button
      const predictionErrors = computed(() =>
        errors.value.filter(e =>
          [
            "pip_range",
            "pump_outside_graphing_range",
            "target_rate_above_max_flow_rate"
          ].includes(e.name)
        )
      );

      const flowRateStatus = c.predictedFlowRateDataSet.status;
      if (
        isNaN(c.hypotheticalCalcs.desiredLiquidRateSTDB.value) ||
        predictionErrors.value.length > 0 ||
        isNaN(
          c.hypotheticalCalcs.pumpCalculations.aggregate
            .requiredTotalDynamicHeadFt.value
        ) ||
        flowRateStatus !== "result-found"
      ) {
        return {
          pumpHz: c.hypotheticalCalcs.adjustedPumpHzFrequency.value,
          totalRequiredBrakeHP: NaN,
          totalRequired60HzBrakeHP: NaN,
          liquidFlowRateSTBD: NaN,
          motorLoadRatio: NaN,
          motorCurrentA: NaN,
          flowVelocityAroundMotorFTSEC: NaN,
          requiredVoltageV: NaN,
          requiredKVA: NaN,
          vfdLoadRatio: NaN
        };
      }
      return {
        pumpHz: c.hypotheticalCalcs.adjustedPumpHzFrequency.value,
        totalRequiredBrakeHP:
          c.hypotheticalCalcs.motorCalculations.totalRequiredBrakeHP.value,
        totalRequired60HzBrakeHP:
          c.hypotheticalCalcs.motorCalculations.totalRequired60HzBrakeHP.value,
        liquidFlowRateSTBD:
          c.hypotheticalCalcs.surfaceFlowRates.value.liquidFlowRateSTBD,
        motorLoadRatio:
          c.hypotheticalCalcs.motorCalculations.motorLoadRatio.value,
        motorCurrentA:
          c.hypotheticalCalcs.motorCalculations.motorCurrentA.value,
        flowVelocityAroundMotorFTSEC:
          c.hypotheticalCalcs.motorCalculations.flowVelocityAroundMotorFTSEC
            .value,
        requiredVoltageV:
          c.hypotheticalCalcs.surfaceDriveCalculations.requiredVoltageV.value,
        requiredKVA:
          c.hypotheticalCalcs.surfaceDriveCalculations.requiredKVA.value,
        vfdLoadRatio:
          c.hypotheticalCalcs.surfaceDriveCalculations.vfdLoadRatio.value
      };
    });
};

export const getStartupBhpRows = (
  context: PumpTornadoChartContext,
  predictedCalcs: PredictiveCalcs,
  destructuredScenario: DestructuredScenario
) => {
  const predictedRows = getPredictedRows(
    destructuredScenario,
    predictedCalcs,
    context.hypotheticalPumpFrequenciesHz
  );

  const designRow = {
    pumpHz: context.scenarioCalculations.adjustedPumpHzFrequency.value,
    totalRequiredBrakeHP:
      context.scenarioCalculations.motorCalculations.totalRequiredBrakeHP.value,
    totalRequired60HzBrakeHP:
      context.scenarioCalculations.motorCalculations.totalRequired60HzBrakeHP
        .value,
    liquidFlowRateSTBD:
      context.scenarioCalculations.surfaceFlowRates.value.liquidFlowRateSTBD,
    motorLoadRatio:
      context.scenarioCalculations.motorCalculations.motorLoadRatio.value,
    motorCurrentA:
      context.scenarioCalculations.motorCalculations.motorCurrentA.value,
    flowVelocityAroundMotorFTSEC:
      context.scenarioCalculations.motorCalculations
        .flowVelocityAroundMotorFTSEC.value,
    requiredVoltageV:
      context.scenarioCalculations.surfaceDriveCalculations.requiredVoltageV
        .value,
    requiredKVA:
      context.scenarioCalculations.surfaceDriveCalculations.requiredKVA.value,
    vfdLoadRatio:
      context.scenarioCalculations.surfaceDriveCalculations.vfdLoadRatio.value
  };

  return [designRow, ...predictedRows];
};
